/**
 * Estado del usuario autenticado — plan 27_09_2026 §4.2.
 *
 * `GET /verify` ya devolvía `{ ok: true, user }`, pero el guard del router
 * descartaba el `user` y ninguna pantalla mostraba quién había entrado.
 * Aquí se cachea esa respuesta en un estado de módulo: varios consumidores
 * (topbar, futuras vistas) comparten una sola petición.
 */

import { ref, readonly } from 'vue';
import api from '@/api';
import { logoutUser } from '@/services/authService.js';

const user = ref(null);
const loading = ref(false);
// Petición en vuelo: si dos componentes piden el usuario a la vez, ambos
// esperan la misma promesa en lugar de disparar dos llamadas a /verify.
let inFlight = null;

async function fetchUser() {
  if (user.value) return user.value;
  if (inFlight) return inFlight;

  loading.value = true;
  inFlight = api
    .get('/verify')
    .then((res) => {
      user.value = res.data?.user || null;
      return user.value;
    })
    .catch(() => {
      user.value = null;
      return null;
    })
    .finally(() => {
      loading.value = false;
      inFlight = null;
    });

  return inFlight;
}

function clear() {
  user.value = null;
  inFlight = null;
}

/**
 * Nombre a mostrar en la topbar. El token firma un usuario del que no
 * conocemos el esquema exacto, así que se prueban los campos habituales y
 * se cae al email antes que a un literal vacío.
 */
function displayName(u) {
  if (!u) return '';
  return u.name || u.userName || u.fullName || u.email || 'Usuario';
}

export function useAuthUser() {
  async function logout() {
    const { success, error } = await logoutUser();
    // Se limpia el estado local aunque el servidor falle: si la cookie sigue
    // viva, el guard lo detectará en la siguiente navegación protegida.
    clear();
    return { success, error };
  }

  return {
    user: readonly(user),
    loading: readonly(loading),
    fetchUser,
    logout,
    clear,
    displayName,
  };
}

export default useAuthUser;
